import { blue } from "@ant-design/colors"
import { Alert, Button, Card, Descriptions, Space, Spin, Tag, Typography } from "antd"
import React, { FC, useContext } from "react"
import ReactMarkdown from "react-markdown"
import { useHistory, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import tinycolor from "tinycolor2"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import { parentScrollToOffset } from "../../helpers/parentIFrame"
import { safeStorage } from "../../helpers/safeStorage"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { SettingsContext } from "../../layouts/Main/MainLayout"
import { URL_BASE, URL_INTEREST_DETAILS, URL_INTEREST_REGISTRATION, URL_LOGIN } from "../../models/url"
import { formatTimeSlotLabel, sortTimeSlots } from "./timeSlotSelection"
import { useInterestListById } from "./interestListData"
import css from "./InterestDetails.module.css"
import { persistLoginFromUrl } from "../login/loginFromUrl"

const { Paragraph, Text, Title } = Typography

/** Öffentliche Angebotsseite zwischen Kurssuche und Interessenanmeldung. */
const InterestDetails: FC = () => {
    const { id } = useParams<{ id: string }>()
    const history = useHistory()
    const { t } = useTranslation()
    const { media } = useMediaSize()
    const customExplanation = useContext(SettingsContext).interestListExplanation?.trim()
    const { list, data, loading, error, notFound, refetch } = useInterestListById(id)

    if (!list) {
        if (error && !data) {
            return (
                <div className={css.page}>
                    <QueryErrorAlert
                        testId='interest-details-error'
                        message={t("interest.details.loadError", "Die Details zur Interessenten-Liste konnten nicht geladen werden.")}
                        onRetry={() => refetch()}
                    />
                </div>
            )
        }

        if (notFound) {
            return (
                <Card className={css.page} data-testid='interest-details-not-found'>
                    <Space direction='vertical' align='center' style={{ width: "100%" }}>
                        <Title level={4}>{t("interest.notFound.title", "Angebot nicht gefunden")}</Title>
                        <Button type='primary' onClick={() => history.push(URL_BASE)}>
                            {t("resultScreen.backToSearch", "Zurück zur Kurssuche")}
                        </Button>
                    </Space>
                </Card>
            )
        }

        return (
            <div data-testid='interest-details-loading' className={css.loading}>
                <Spin spinning={loading} />
            </div>
        )
    }

    const detailsUrl = URL_INTEREST_DETAILS.replace(":id", list.id)
    const registrationUrl = URL_INTEREST_REGISTRATION.replace(":id", list.id)
    const isLoggedIn = Boolean(safeStorage.getItem("loggedInUser"))
    const timeSlots = sortTimeSlots(list.timeSlots ?? [])
    const courseTypeColor = list.courseType.color || blue[1]
    const headerTextColor = tinycolor(courseTypeColor).isLight() ? "rgba(0, 0, 0, 0.78)" : "rgba(255, 255, 255, 0.92)"

    const continueToRegistration = () => {
        parentScrollToOffset(0, 0)
        if (isLoggedIn) {
            history.push(registrationUrl)
            return
        }
        // Hash-History verwirft location.state. Deshalb das Rücksprungziel vor
        // der Navigation persistieren (inkl. TTL für Passwordless/Magic-Link).
        persistLoginFromUrl({ pathname: detailsUrl })
        history.push(URL_LOGIN)
    }

    const renderCta = (testId: string, block = false) => (
        <Button
            type='primary'
            block={block}
            disabled={list.isFull}
            data-testid={testId}
            onClick={continueToRegistration}
        >
            {list.isFull ? t("interest.ctaFull", "Liste ist voll") : t("interest.details.cta", "Interesse bekunden")}
        </Button>
    )

    return (
        <Card
            data-testid='interest-details'
            className={css.page}
            bordered={false}
            headStyle={{ background: courseTypeColor, color: headerTextColor }}
            title={
                <div className={css.header}>
                    <div className={css.headerText}>
                        <Title level={1} className={css.title} style={{ color: headerTextColor }}>
                            {t("interest.details.title", "Details zur Interessenten-Liste")}
                        </Title>
                        <Space size='small' wrap>
                            <Tag color='blue'>{t("interest.badge", "Interessenten-Liste")}</Tag>
                            <Text strong style={{ color: headerTextColor }}>
                                {list.courseType.name}
                            </Text>
                        </Space>
                    </div>
                    <div className={css.headerCta}>{renderCta("interest-details-cta-header", media === "xs")}</div>
                </div>
            }
            actions={[
                <div key='interest-details-action' className={css.footerCta}>
                    {renderCta("interest-details-cta", true)}
                </div>,
            ]}
        >
            <Space direction='vertical' size='large' className={css.body}>
                <Alert
                    type='info'
                    showIcon
                    message={t("interest.details.explanationTitle", "Was bedeutet die Interessenten-Liste?")}
                    description={
                        customExplanation ? (
                            // Eigener Text der Schule (Markdown ohne HTML, wie die Kursbeschreibung)
                            <ReactMarkdown className={css.explanation}>{customExplanation}</ReactMarkdown>
                        ) : (
                            t(
                                "interest.details.explanation",
                                "Mit dem Eintrag auf der Interessenten-Liste bekundest du unverbindlich dein Interesse an dieser Kursart an diesem Standort. Sobald ein passendes Angebot verfügbar ist, kann der Kursanbieter dich kontaktieren. Der Eintrag ist keine Kursbuchung und reserviert keinen Kursplatz."
                            )
                        )
                    }
                />

                <section className={css.section} aria-labelledby='interest-offer-data'>
                    <Title id='interest-offer-data' level={4}>
                        {t("interest.details.dataTitle", "Angebotsdaten")}
                    </Title>
                    <Descriptions bordered={media === "xs"} size={media === "xs" ? "small" : "default"} column={{ xs: 1, sm: 1, md: 2 }}>
                        <Descriptions.Item label={t("interest.details.courseType", "Kursart")}>{list.courseType.name}</Descriptions.Item>
                        <Descriptions.Item label={t("interest.details.location", "Standort")}>{list.location.name}</Descriptions.Item>
                        {list.location.city && <Descriptions.Item label={t("interest.details.city", "Ort")}>{list.location.city}</Descriptions.Item>}
                    </Descriptions>
                </section>

                {list.description && (
                    <section className={css.section} aria-labelledby='interest-description'>
                        <Title id='interest-description' level={4}>
                            {t("interest.details.descriptionTitle", "Weitere Informationen")}
                        </Title>
                        <Paragraph className={css.description}>{list.description}</Paragraph>
                    </section>
                )}

                {timeSlots.length > 0 && (
                    <section className={css.section} aria-labelledby='interest-time-slots'>
                        <Title id='interest-time-slots' level={4}>
                            {t("interest.details.timeSlotsTitle", "Mögliche Zeiten")}
                        </Title>
                        <Space size={[8, 8]} wrap data-testid='interest-details-time-slots'>
                            {timeSlots.map((slot) => (
                                <Tag key={slot.id}>{formatTimeSlotLabel(slot)}</Tag>
                            ))}
                        </Space>
                    </section>
                )}
            </Space>
        </Card>
    )
}

export default InterestDetails
